<script lang="ts" setup>
/*
  See MNTX-859 why we handle "Webshop" stock in a different way
*/
import * as Sentry from "@sentry/vue";
import { useProductPageStore } from "~/stores/main";

// -----------------------
// props
// -----------------------
const props = withDefaults(
  defineProps<{
    show?: boolean;
    store?: any;
  }>(),
  {
    show: true,
    store: null,
  },
);

// -----------------------
// composables
// -----------------------
const productPageStore = props.store || useProductPageStore();
const { t } = useI18n();

// -----------------------
// reactive properties
// -----------------------
const { data: storesRef } = await useApiFetch<{ data: any[] }>(
  "stores?per_page=100",
);

// -----------------------
// computed properties
// -----------------------
const variantRef = computed(() => {
  if (productPageStore.size === null || productPageStore.color === null) {
    return null;
  }

  return productPageStore.masterProduct?.products.find(
    (variant: Record<any, any>) => {
      return (
        variant.product.product_meret_property === productPageStore.size &&
        variant.product.product_szin_property === productPageStore.color
      );
    },
  );
});

// -----------------------
// helper functions
// -----------------------
const getStockStatus = (slug: string) => {
  if (slug === "") return "out_of_stock";
  // if (slug === "miskolc") return "out_of_stock"; // MNTX-688

  const product = variantRef?.value?.product;
  if (!product) return "out_of_stock";

  const stockInStores = product?.product_keszlet_property;

  let stock = null;
  if (slug === "__webshop") {
    const webshopStock =
      parseInt(
        stockInStores?.find(
          (store: Record<string, any>) => store.key === "szentendre",
        ).value,
      ) +
      parseInt(
        stockInStores?.find(
          (store: Record<string, any>) => store.key === "outlet",
        ).value,
      ) +
      parseInt(
        stockInStores?.find(
          (store: Record<string, any>) => store.key === "webshop",
        ).value,
      );
    stock = { key: "__webshop", value: webshopStock };
  } else {
    stock = stockInStores?.find(
      (store: Record<string, any>) => store.key === slug,
    );
  }

  if (!stock) {
    Sentry.captureEvent({
      message: `Stock status is unavailable (${slug})`,
      level: "error",
      extra: {
        product,
        stockInStores,
      },
    });

    return "out_of_stock";
  } else if (stock.value === 0) {
    return "out_of_stock";
  } else if (stock.value === 1) {
    return "last_piece";
  } else {
    return "in_stock";
  }
};

const webshopStock = computed(() => {
  return (
    productPageStore.masterProduct?.stock_per_store.szentendre +
    productPageStore.masterProduct?.stock_per_store.outlet +
    productPageStore.masterProduct?.stock_per_store.webshop
  );
});
</script>

<template>
  <div>
    <div v-if="storesRef?.data && productPageStore.masterProduct">
      <div class="flex justify-between mt-5 xl:mt-5.5">
        <h3 class="font-bold text-base lg:text-xl">
          {{ t("stock") }}
        </h3>
      </div>
      <div class="mt-4 xl:mt-[22px]">
        <div>
          <span v-for="storeItem in storesRef?.data" :key="storeItem.id">
            <BaseButton
              v-if="['szentendre', 'outlet'].includes(storeItem.slug) === false"
              type="plain"
              font-weight="normal"
              :title="t(getStockStatus(storeItem.slug))"
              :disabled="getStockStatus(storeItem.slug) === 'out_of_stock'"
              class="!bg-brand-secondary shadow-base mr-2 mb-2 min-w-[95px] min-h-[37px] xl:min-h-[44px] cursor-default text-sm"
            >
              <i
                class="ml-[6px] text-xs"
                :class="{
                  'text-brand-secondary':
                    getStockStatus(storeItem.slug) === 'in_stock',
                  'm-icon-warning':
                    getStockStatus(storeItem.slug) === 'last_piece',
                  'm-icon-out_of_stock':
                    getStockStatus(storeItem.slug) === 'out_of_stock',
                }"
              ></i>
              {{ storeItem.name }}
            </BaseButton>
          </span>
          <span v-if="webshopStock > 0">
            <BaseButton
              type="plain"
              font-weight="normal"
              :title="t(getStockStatus('__webshop'))"
              :disabled="getStockStatus('__webshop') === 'out_of_stock'"
              class="!bg-brand-secondary shadow-base mr-2 mb-2 min-w-[95px] min-h-[37px] xl:min-h-[44px] cursor-default text-sm"
            >
              <i
                class="ml-[6px] text-xs"
                :class="{
                  'm-icon-check_mark text-brand-secondary':
                    getStockStatus('__webshop') === 'in_stock',
                  'm-icon-warning':
                    getStockStatus('__webshop') === 'last_piece',
                  'm-icon-out_of_stock':
                    getStockStatus('__webshop') === 'out_of_stock',
                }"
              ></i>
              Központi raktár
            </BaseButton>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
